import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Alert, 
  Space, 
  Typography, 
  message
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  LockOutlined, 
  SafetyOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  QqOutlined
} from '@ant-design/icons';
import API from '../services/api';
import './RegisterPage.css';

const { Title, Text } = Typography;

const RegisterPage = () => {
  const [form] = Form.useForm();
  const [resendForm] = Form.useForm();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    verificationCode: '',
    qq: '',
    captchaCode: ''
  });
  
  const [captcha, setCaptcha] = useState({
    captchaId: '',
    captchaSvg: '',
    expiresIn: 0
  });
  
  const [resendCaptcha, setResendCaptcha] = useState({
    captchaId: '',
    captchaSvg: '',
    expiresIn: 0
  });
  
  const [current, setCurrent] = useState(0); // 当前步骤
  const [loading, setLoading] = useState(false);
  const [waitTime, setWaitTime] = useState(0);

  // 获取图形验证码
  const getCaptcha = async () => {
    try {
      const response = await API.get('/api/register/captcha');
      setCaptcha({
        captchaId: response.data.captchaId,
        captchaSvg: response.data.captchaSvg,
        expiresIn: response.data.expiresIn
      });
    } catch (error) {
      message.error('获取验证码失败：' + (error.response?.data?.message || '网络错误'));
    }
  };

  // 获取重发用的图形验证码
  const getResendCaptcha = async () => {
    try {
      const response = await API.get('/api/register/captcha');
      setResendCaptcha({
        captchaId: response.data.captchaId,
        captchaSvg: response.data.captchaSvg,
        expiresIn: response.data.expiresIn
      });
    } catch (error) {
      message.error('获取验证码失败：' + (error.response?.data?.message || '网络错误'));
    }
  };

  // 组件加载时获取验证码
  useEffect(() => {
    getCaptcha();
    getResendCaptcha();
  }, []);

  // 倒计时处理
  useEffect(() => {
    if (waitTime > 0) {
      const timer = setTimeout(() => setWaitTime(waitTime - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [waitTime]);

  // 发送验证码
  const sendVerificationCode = async (values) => {
    setLoading(true);
    
    try {
      const response = await API.post('/api/register/send-verification-code', {
        username: values.username,
        email: values.email,
        captchaId: captcha.captchaId,
        captchaCode: values.captchaCode
      });
      
      message.success(response.data.message);
      setFormData({ ...formData, ...values });
      setCurrent(1);
      
      // 设置倒计时，防止频繁重新发送验证码
      const waitTime = response.data.waitTime || 60; // 如果服务器返回等待时间，使用服务器的，否则默认60秒
      setWaitTime(waitTime);
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = errorData?.message || '发送失败';
      
      // 如果错误信息包含验证码相关内容，提供更明确的提示
      if (errorMessage.includes('验证码') || errorMessage.includes('captcha')) {
        errorMessage = '图形验证码错误，请重新输入';
      }
      
      message.error(errorMessage);
      
      // 处理频率限制
      if (errorData?.rateLimited && errorData?.waitTime) {
        setWaitTime(errorData.waitTime);
      }
      
      // 重新获取图形验证码
      getCaptcha();
      form.setFieldsValue({ captchaCode: '' });
    } finally {
      setLoading(false);
    }
  };

  // 重新发送验证码
  const resendVerificationCode = async (captchaCode) => {
    if (!captchaCode) {
      message.error('请输入图形验证码');
      return;
    }

    setLoading(true);
    try {
      const response = await API.post('/api/register/resend-verification-code', {
        username: formData.username,
        email: formData.email,
        captchaId: resendCaptcha.captchaId,
        captchaCode: captchaCode
      });
      
      message.success(response.data.message);
      
      // 设置倒计时，防止频繁重新发送验证码
      const waitTime = response.data.waitTime || 60; // 如果服务器返回等待时间，使用服务器的，否则默认60秒
      setWaitTime(waitTime);
      
      // 清除重新发送表单的验证码字段并刷新验证码
      resendForm.setFieldsValue({ resendCaptchaCode: '' });
      getResendCaptcha();
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = errorData?.message || '重新发送失败';
      
      // 如果错误信息包含验证码相关内容，提供更明确的提示
      if (errorMessage.includes('验证码') || errorMessage.includes('captcha')) {
        errorMessage = '图形验证码错误，请重新输入';
      }
      
      message.error(errorMessage);
      
      if (errorData?.rateLimited && errorData?.waitTime) {
        setWaitTime(errorData.waitTime);
      }
      
      // 无论什么错误都刷新验证码，确保验证码状态同步
      getResendCaptcha();
      // 清除重新发送表单的验证码字段
      resendForm.setFieldsValue({ resendCaptchaCode: '' });
    } finally {
      setLoading(false);
    }
  };

  // 完成注册
  const completeRegistration = async (values) => {
    setLoading(true);
    try {
      const response = await API.post('/api/register/verify-email-and-register', {
        ...formData,
        ...values
      });
      
      message.success(response.data.message + ' 即将跳转到登录页面...');
      setCurrent(2);
      
      // 注册成功，延迟跳转到登录页
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      message.error(error.response?.data?.message || '注册失败');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (current) {
      case 0:
        return (
          <Card className="step-card">
            <div className="step-header">
              <Title level={3}>填写注册信息</Title>
              <Text type="secondary">请填写用户名、邮箱和图形验证码</Text>
            </div>
            
            <Form
              form={form}
              layout="vertical"
              onFinish={sendVerificationCode}
              size="large"
              className="register-form"
            >
              <Form.Item
                name="username"
                label="用户名"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' }
                ]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="请输入用户名" 
                  className="custom-input"
                />
              </Form.Item>

              <Form.Item
                name="email"
                label="邮箱地址"
                rules={[
                  { required: true, message: '请输入邮箱地址' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="请输入邮箱地址" 
                  className="custom-input"
                />
              </Form.Item>

              <Form.Item
                name="captchaCode"
                label="图形验证码"
                rules={[{ required: true, message: '请输入图形验证码' }]}
              >
                <div className="captcha-container">
                  <div 
                    className="captcha-image"
                    dangerouslySetInnerHTML={{ __html: captcha.captchaSvg }}
                    onClick={getCaptcha}
                    title="点击刷新验证码"
                  />
                  <Input 
                    prefix={<SafetyOutlined />}
                    placeholder="请输入验证码"
                    maxLength={4}
                    className="captcha-input"
                    style={{ textTransform: 'uppercase' }}
                  />
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={getCaptcha}
                    className="refresh-btn"
                  >
                    刷新
                  </Button>
                </div>
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  disabled={waitTime > 0}
                  className="submit-btn"
                  block
                  size="large"
                >
                  {waitTime > 0 ? `等待 ${waitTime} 秒` : '发送验证码'}
                </Button>
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
                <Button 
                  type="link" 
                  onClick={() => window.location.href = '/login'}
                  style={{ padding: 0, height: 'auto', fontSize: '14px' }}
                >
                  已有账号？返回登录
                </Button>
              </Form.Item>
            </Form>
          </Card>
        );

      case 1:
        return (
          <Card className="step-card">
            <div className="step-header">
              <Title level={3}>验证邮箱并设置密码</Title>
              <Alert
                message="验证码已发送"
                description={`验证码已发送到 ${formData.email}，请查收邮件`}
                type="info"
                showIcon
                className="email-alert"
              />
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={completeRegistration}
              size="large"
              className="register-form"
            >
              <Form.Item
                name="password"
                label="密码"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6位字符' }
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="请输入密码（至少6位）" 
                  className="custom-input"
                />
              </Form.Item>

              <Form.Item
                name="verificationCode"
                label="邮箱验证码"
                rules={[
                  { required: true, message: '请输入邮箱验证码' },
                  { len: 6, message: '验证码为6位数字' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined />}
                  placeholder="请输入6位验证码"
                  maxLength={6}
                  className="verification-input"
                />
              </Form.Item>

              <Form.Item
                name="qq"
                label="QQ号（可选）"
              >
                <Input 
                  prefix={<QqOutlined />} 
                  placeholder="请输入QQ号" 
                  className="custom-input"
                />
              </Form.Item>

              <Form.Item>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    className="submit-btn"
                    block
                    size="large"
                  >
                    完成注册
                  </Button>
                  
                  <Button 
                    onClick={() => setCurrent(0)}
                    block
                    size="large"
                  >
                    返回修改信息
                  </Button>
                </Space>
              </Form.Item>
            </Form>

            {/* 重新发送验证码 */}
            <Card className="resend-card" size="small">
              <Title level={5}>没有收到验证码？</Title>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text type="secondary">请先输入图形验证码，然后点击重新发送</Text>
                
                <Form 
                  form={resendForm}
                  onFinish={(values) => resendVerificationCode(values.resendCaptchaCode)}
                >
                  <div className="captcha-container">
                    <div 
                      className="captcha-image small"
                      dangerouslySetInnerHTML={{ __html: resendCaptcha.captchaSvg }}
                      onClick={getResendCaptcha}
                      title="点击刷新验证码"
                    />
                    <Form.Item 
                      name="resendCaptchaCode"
                      style={{ margin: 0, flex: 1 }}
                      rules={[{ required: true, message: '请输入图形验证码' }]}
                    >
                      <Input 
                        placeholder="图形验证码"
                        maxLength={4}
                        style={{ textTransform: 'uppercase' }}
                      />
                    </Form.Item>
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={getResendCaptcha}
                      size="small"
                    />
                  </div>
                  
                  <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
                    <Button 
                      type="dashed"
                      htmlType="submit"
                      loading={loading}
                      disabled={waitTime > 0}
                      block
                    >
                      {waitTime > 0 ? `等待 ${waitTime} 秒` : '重新发送验证码'}
                    </Button>
                  </Form.Item>
                </Form>
              </Space>
            </Card>
          </Card>
        );

      case 2:
        return (
          <Card className="success-card">
            <div className="success-content">
              <CheckCircleOutlined className="success-icon" />
              <Title level={2}>注册成功！</Title>
              <Text type="secondary">您的账号已创建成功，即将跳转到登录页面...</Text>
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        {/* 页面标题 */}
        <div className="page-header">
          <Title level={1} className="page-title">
            用户注册
          </Title>
        </div>

        {/* 步骤内容 */}
        <div className="step-content">
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
